import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import Dialog from "@mui/material/Dialog"
import GrantAccessModal from "../../pages/Common/GrantAccessModal"
import { getLocationModal, setLocationModal } from "../../pages/Common/commonSlice"

// Location and camera access gate the commissioning and survey lanes: both
// stamp photos with coordinates, so access is granted on the auth screen before
// login. The site-visit lane asks at check-in/check-out time instead
// (useSvLocation) and so stays on the landing allowlist.
const LocationGate = () => {
    const dispatch = useDispatch()
    const { pathname } = useLocation()
    const onLanding =
        pathname === "/" || pathname === "/home" || pathname.startsWith("/site-visit")
    // Either lane's entry point always asks for access before login, even if
    // old coordinates are still in storage.
    const onAuthEntry =
        pathname === "/commissioning-form/auth" || pathname === "/site-survey/auth"

    const isOpen = useSelector(getLocationModal)
    const { t: translate } = useTranslation()

    const [isLoading, setIsLoading] = useState(false)
    /**
     * Permission is denied at browser level. Re-requesting never prompts — the
     * browser answers with the same error and shows no UI — so the modal has to
     * explain itself rather than appear to do nothing. Pages cannot open the
     * browser's own permission UI, so pointing the user at it is the ceiling.
     */
    const [blocked, setBlocked] = useState(false)
    const [error, setError] = useState("")

    /** "granted" | "prompt" | "denied", or "unknown" where the API is absent. */
    const permissionState = async () => {
        try {
            const status = await navigator.permissions?.query({
                name: "geolocation" as PermissionName,
            })
            return status?.state ?? "unknown"
        } catch {
            // Permissions API unavailable (older Safari) — learn on request.
            return "unknown"
        }
    }

    const checkPermission = () =>
        permissionState().then(state => setBlocked(state === "denied"))

    // Changing the setting in the browser's own UI fires this, so the modal
    // recovers by itself instead of waiting for another failed tap.
    useEffect(() => {
        let status: PermissionStatus | undefined
        let cancelled = false

        navigator.permissions
            ?.query({ name: "geolocation" as PermissionName })
            .then(result => {
                if (cancelled) return
                status = result
                result.onchange = () => {
                    const denied = result.state === "denied"
                    setBlocked(denied)
                    if (!denied) setError("")
                }
            })
            .catch(() => { /* nothing to watch */ })

        return () => {
            cancelled = true
            if (status) status.onchange = null
        }
    }, [])

    /**
     * Not a denial: no fix indoors, or the 10s high-accuracy request timing
     * out. Reported separately so a basement survey does not read as "you
     * denied permission".
     */
    const describeFailure = (failure: GeolocationPositionError) =>
        failure.code === failure.TIMEOUT
            ? translate("locationTimeout")
            : translate("locationUnavailable")

    const [latitude] = useState<any>(() => {
        const saved = localStorage.getItem("latitude")
        return saved ? JSON.parse(saved) : null
    })

    const [longitude] = useState<any>(() => {
        const saved = localStorage.getItem("longitude")
        return saved ? JSON.parse(saved) : null
    })

    const requestLocation = () => {
        // Read storage now rather than trusting the values captured when this
        // component mounted: access is granted on the auth screen, so by the
        // time the first form page runs this the mount-time copy is stale and
        // the modal would reopen over a form that already has coordinates.
        const storedLat = localStorage.getItem("latitude")
        const storedLng = localStorage.getItem("longitude")
        dispatch(setLocationModal({ locationModal: !(storedLat && storedLng) }))

        if (!navigator.geolocation) {
            dispatch(setLocationModal({ locationModal: false }))
            return
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                localStorage.setItem("latitude", `${position.coords.latitude}`)
                localStorage.setItem("longitude", `${position.coords.longitude}`)
                setBlocked(false)
                setError("")
                dispatch(setLocationModal({ locationModal: false }))
            },
            (failure) => {
                if (failure.code === failure.PERMISSION_DENIED) setBlocked(true)
                else setError(describeFailure(failure))
                dispatch(setLocationModal({ locationModal: true }))
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            }
        )
    }

    useEffect(() => {
        if (onLanding) {
            dispatch(setLocationModal({ locationModal: false }))
            return
        }
        if (onAuthEntry) {
            // Know up front whether the browser will even prompt, so the first
            // tap on Grant access can explain itself rather than do nothing.
            checkPermission()
            dispatch(setLocationModal({ locationModal: true }))
            return
        }
        dispatch(setLocationModal({ locationModal: true }))
        requestLocation()
    }, [latitude, longitude, onLanding, onAuthEntry])

    const handleAccess = async () => {
        if (!navigator.geolocation) {
            dispatch(setLocationModal({ locationModal: true }))
            return
        }
        setIsLoading(true)
        setError("")

        /**
         * Ask the Permissions API before requesting a fix.
         *
         * While permission is denied `getCurrentPosition` never prompts and
         * calls back with the same error, so every state write below lands on
         * an unchanged value and the button looks dead. Checking first means a
         * retry that cannot possibly work says so.
         */
        if ((await permissionState()) === "denied") {
            setIsLoading(false)
            setBlocked(true)
            return
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                localStorage.setItem("latitude", `${position.coords.latitude}`)
                localStorage.setItem("longitude", `${position.coords.longitude}`)
                setBlocked(false)
                setIsLoading(false)
                dispatch(setLocationModal({ locationModal: false }))
            },
            (failure) => {
                // Stop the spinner on failure — leaving it true stranded the
                // button mid-load with no error and no way to retry.
                setIsLoading(false)
                // Covers browsers with no Permissions API, where the check
                // above returns "unknown" and falls through to here.
                if (failure.code === failure.PERMISSION_DENIED) setBlocked(true)
                else setError(describeFailure(failure))
                dispatch(setLocationModal({ locationModal: true }))
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0
            }
        )
    }

    return (
        <Dialog maxWidth="sm" open={isOpen && !onLanding}>
            <GrantAccessModal
                handleAccess={handleAccess}
                isLoading={isLoading}
                blocked={blocked}
                error={error}
                onReload={() => window.location.reload()}
            />
        </Dialog>
    )
}

export default LocationGate
